import {
	LightbulbLit,
	LogoText2,
	NavigationalLeftArrow,
	PlaneTransparent
} from '@/resources/assets/images';
import Button from '@/components/Button/newButtons';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import { useOnboardingContext } from '../context/OnboardingContext';

export default function OnboardingSignature() {
	const { onboardingData, setOnboardingData, nextPage, previousPage } =
		useOnboardingContext();
	const [signatureError, setSignatureError] = useState<string | null>(null);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	const handleNextClick = () => {
		const length = onboardingData.signature?.length ?? 0;

		if (length < 1) {
			setSignatureError(
				'Please fill in the signature before continuing.'
			);
			return;
		}

		if (length > 1000) {
			setSignatureError('Your signature must be under 1000 characters.');
			return;
		}

		setSignatureError(null);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Onboarding Flow - Submitted Signature',
			properties: {
				page: 'Step 4: Signature Page',
				signature: onboardingData.signature
			}
		});
		nextPage();
	};

	const variant =
		user?.features?.onboardingTestGroup === 'test'
			? 'OnboardingFlowA'
			: 'OnboardingFlowB';

	const getCopy = () => {
		if (variant === 'OnboardingFlowA') {
			return {
				title: "Who's this coming from?",
				body: (
					<>
						<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
							We&apos;ll sign your emails with your{' '}
							<span className="font-bold text-dm-gold">
								name, company
							</span>{' '}
							and{' '}
							<span className="font-bold text-dm-gold">
								phone number
							</span>{' '}
							— so it&apos;s personal, not spammy. <br />
							This builds trust and improves reply rates.
						</span>
					</>
				)
			};
		}

		// Variant B (default)
		return {
			title: 'Add Your Signature',
			body: (
				<>
					<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
						This builds trust and improves reply rates. <br />
						Just include your{' '}
						<span className="font-bold text-dm-gold">
							name, company,
						</span>{' '}
						and{' '}
						<span className="font-bold text-dm-gold">
							phone number
						</span>
					</span>
				</>
			)
		};
	};

	const copy = getCopy();

	return (
		<div className="h-full w-full overflow-hidden">
			{/* Top Header */}
			<div className="h-[3.75rem] w-full overflow-hidden rounded-tl-lg rounded-tr-lg border-b border-t border-dm-gray px-4 sm:h-[6.375rem] sm:px-20 lg:rounded-tr-none lg:border-l">
				<div className="flex h-full w-full items-center">
					<div className="mx-auto flex w-full max-w-[79rem] items-center justify-between">
						<div className="flex items-center gap-4">
							<LightbulbLit className="h-[2rem] w-[1.375rem] shrink-0" />
							<h1 className="font-barlow text-lg font-semibold leading-[1.375rem] text-dm-white">
								Step 4 of 4: Your Signature
							</h1>
						</div>

						<LogoText2 className="h-6 w-[6.5625rem] transition-all duration-300 3xl:-mr-[3.75rem]" />
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="h-full border-dm-gray px-4 py-6 sm:py-10 sm:pl-12 sm:pr-16 lg:border-l">
				<div
					data-flow-a={variant === 'OnboardingFlowA'}
					data-flow-b={variant === 'OnboardingFlowB'}
					className="mx-auto flex w-full max-w-[79rem] flex-col overflow-hidden rounded-2xl border border-dm-gray bg-dm-black3/50 px-4 pt-8 data-[flow-a=true]:min-h-[28.75rem] data-[flow-b=true]:min-h-[26.75rem] sm:px-20"
				>
					<Button
						type="button"
						variant="text"
						className="flex rounded-lg p-0 text-dm-white sm:hidden"
						onClick={previousPage}
						leftIcon={<NavigationalLeftArrow />}
					>
						Back
					</Button>
					<div className="flex flex-1 flex-col gap-10 overflow-auto pb-8 sm:overflow-visible sm:pb-0">
						<div className="flex flex-col gap-4">
							{/* Step Title */}
							<span className="font-barlow text-xl font-semibold leading-8 tracking-normal text-dm-white sm:text-2xl sm:leading-10">
								{copy.title}
							</span>

							{/* Body text */}
							<div className="max-w-[41rem] font-barlow text-sm font-normal leading-7 text-dm-white sm:text-base sm:leading-8">
								{copy.body}
							</div>

							{/* Textarea */}
							<div className="flex flex-col gap-1">
								<textarea
									autoFocus
									placeholder="Enter your signature here"
									className="text-small h-[4.75rem] w-full max-w-[30rem] resize-none rounded-[.25] border border-gray-300 bg-white px-3 py-2 font-barlow font-normal leading-[1.188] tracking-normal text-black focus:outline-none sm:text-base"
									value={onboardingData.signature}
									onChange={e =>
										setOnboardingData({
											signature: e.target.value
										})
									}
								/>
								<div className="-mt-1 h-[1rem]">
									{signatureError && (
										<ErrorMessage
											message={signatureError}
										/>
									)}
								</div>
							</div>
						</div>

						{/* Button */}
						<div className="flex flex-col justify-start gap-7 sm:flex-row sm:gap-14">
							<Button
								type="button"
								variant="secondary"
								className="hidden rounded-lg px-6 text-white sm:flex"
								onClick={previousPage}
							>
								Go Back
							</Button>
							<Button
								type="button"
								variant="primary"
								className="rounded-lg px-6 text-black"
								onClick={handleNextClick}
								leftIcon={
									<PlaneTransparent className="text-black" />
								}
							>
								Next: Confirmation
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
